/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import Api2LastValueData from '@/components/api/Api2LastValueData';
import Api2TimeSeriesData from '@/components/api/Api2TimeSeriesData';
import CodeBlock from '@/components/code/CodeBlock';
import AppLoader from '@/components/feedback/AppLoader';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import { useI18n } from '@/context/I18nContext';

/* Import types */
import type { Api2ValueDataItem } from '@/lib/overview/api2ValueData';

interface Api2DataLayoutsTabProps {
    theme: 'light' | 'dark';
}

const API_TIME_SERIES_DATA_EXAMPLES = [
    {
        label: 'Single value per box',
        mode: 'single' as const,
        code: `<Api2TimeSeriesData
    from="NOW-4WEEKS"
    to="NOW"
    urn={[
        'calc.AD.N.Z01.P.PV.ratio_30',
        'calc.AD.N.Z02.P.PV.ratio_30',
    ]}
    aggregation="last"
    title="Ratio of Measurement to Prediction"
    conversion={100}
    unit="%"
>
    {({ items, isLoading }) => /* render custom content */}
</Api2TimeSeriesData>`,
    },
    {
        label: 'Three values per box',
        mode: 'multi' as const,
        code: `<Api2TimeSeriesData
    from="NOW-4WEEKS"
    to="NOW"
    urn={[
        ['calc.AD.N.Z01.P.PV.ratio_30', 'calc.AD.N.Z01.P.PV.ref_30', 'calc.AD.N.Z01.P.PV.recent_30'],
        ['calc.AD.N.Z02.P.PV.ratio_30', 'calc.AD.N.Z02.P.PV.ref_30', 'calc.AD.N.Z02.P.PV.recent_30'],
    ]}
    aggregation="last"
    conversion={[100, 1 / 1000000, 1 / 1000000]}
    unit={["%", "kWh", "kWh"]}
>
    {({ items, isLoading }) => /* render custom content */}
</Api2TimeSeriesData>`,
    },
];

const LAST_VALUE_DATA_CODE = `<Api2LastValueData
    date="NOW"
    urn={[
        'calc.AD.N.Z01.P.PV.ratio_30',
        'calc.AD.N.Z01.P.PV.ref_30',
    ]}
    conversion={[100, 1 / 1000000]}
    unit={["%", "MWh"]}
>
    {({ items, isLoading }) => /* render custom content */}
</Api2LastValueData>`;

function ResolvedDataPreview({ items, isLoading }: { items: Api2ValueDataItem[]; isLoading: boolean }) {
    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <AppLoader label="Loading..." />
            </div>
        );
    }

    return (
        <div className="grid w-full gap-4 md:grid-cols-2 xl:grid-cols-4">
            {items.map((item) => {
                const errorMessages = Array.isArray(item.errorMessage)
                    ? item.errorMessage
                    : [item.errorMessage];

                return (
                    <div key={item.key} className="rounded-box border border-base-300 bg-base-100 p-4 shadow-sm">
                        <p className="font-medium text-base-content">{item.title ?? item.urn.getFirst()}</p>
                        {item.urn.values().map((urn, index) => <p key={`${item.key}-${urn}-${index}`}>{urn}</p>)}
                        {item.formattedValue.values().map((value, index) => (
                            <p key={`${item.key}-value-${index}`} className="text-base-content/80">
                                {value ?? '-'}{item.unit.get(index) ? ` ${item.unit.get(index)}` : ''}
                            </p>
                        ))}
                        {errorMessages.filter(Boolean).map((errorMessage, index) => (
                            <p key={`${item.key}-error-${index}`} className="mt-2 text-error">{errorMessage}</p>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function Api2DataLayoutsTab({ theme }: Api2DataLayoutsTabProps) {
    const { t } = useI18n();
    const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
    const selectedExample = API_TIME_SERIES_DATA_EXAMPLES[selectedExampleIndex];
    const isMultiValueExample = selectedExample.mode === 'multi';

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="api-data" />

            <section className="layouts-section">
                <h3>Api2LastValueData</h3>
                <p>{t('Loads current values and passes resolved data into a render function without imposing a KPI layout.')}</p>
                <h4>Code example</h4>
                <CodeBlock code={LAST_VALUE_DATA_CODE} theme={theme} />
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-stretch justify-stretch p-6">
                    <Api2LastValueData
                        date="NOW"
                        urn={['calc.AD.N.Z01.P.PV.ratio_30', 'calc.AD.N.Z01.P.PV.ref_30']}
                        title={['Ratio of Measurement to Prediction', 'Measured PV value']}
                        conversion={[100, 1 / 1000000]}
                        unit={['%', 'MWh']}
                    >
                        {({ items, isLoading }) => <ResolvedDataPreview items={items} isLoading={isLoading} />}
                    </Api2LastValueData>
                </section>
            </section>

            <section className="layouts-section">
                <h3>Api2TimeSeriesData</h3>
                <p>Loads and aggregates time series before passing resolved data into a render function.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="api2-time-series-data-example">API data example</label>
                    <select
                        id="api2-time-series-data-example"
                        className="select select-bordered w-auto"
                        value={selectedExampleIndex}
                        onChange={(event) => {
                            setSelectedExampleIndex(Number(event.target.value));
                        }}
                    >
                        {API_TIME_SERIES_DATA_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <CodeBlock code={selectedExample.code} theme={theme} />
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-stretch justify-stretch p-6">
                    <Api2TimeSeriesData
                        from="NOW-4WEEKS"
                        to="NOW"
                        urn={
                            isMultiValueExample
                                ? [
                                    ['calc.AD.N.Z01.P.PV.ratio_30', 'calc.AD.N.Z01.P.PV.ref_30', 'calc.AD.N.Z01.P.PV.recent_30'],
                                    ['calc.AD.N.Z02.P.PV.ratio_30', 'calc.AD.N.Z02.P.PV.ref_30', 'calc.AD.N.Z02.P.PV.recent_30'],
                                    ['calc.AD.N.Z03.P.PV.ratio_30', 'calc.AD.N.Z03.P.PV.ref_30', 'calc.AD.N.Z03.P.PV.recent_30'],
                                    ['calc.AD.BA2.Z34.P.PV.ratio_30', 'calc.AD.BA2.Z34.P.PV.ref_30', 'calc.AD.BA2.Z34.P.PV.recent_30'],
                                ]
                                : [
                                    'calc.AD.N.Z01.P.PV.ratio_30',
                                    'calc.AD.N.Z02.P.PV.ratio_30',
                                    'calc.AD.N.Z03.P.PV.ratio_30',
                                    'calc.AD.BA2.Z34.P.PV.ratio_30',
                                ]
                        }
                        aggregation="last"
                        title="Ratio of Measurement to Prediction"
                        conversion={isMultiValueExample ? [100, 1 / 1000000, 1 / 1000000] : 100}
                        unit={isMultiValueExample ? ['%', 'kWh', 'kWh'] : '%'}
                    >
                        {({ items, isLoading }) => <ResolvedDataPreview items={items} isLoading={isLoading} />}
                    </Api2TimeSeriesData>
                </section>
            </section>
        </div>
    );
}

export default Api2DataLayoutsTab;
